import { executeSearchRequest } from 'tio-data/requests/search';
import type { Store } from 'tio-data';
import {
  OpenSearchQueryBuilder,
  classifySearchToken,
  type FilterClause,
} from './open-search-builder.ts';
import type { OpenSearchHit, OpenSearchResponse } from 'tio-data/data/resources/search.ext';

const DEFAULT_LIMIT = 10;
const EMPLOYEE_NAME_FIELDS = [
  'employees_nested.first_name_text',
  'employees_nested.last_name_text',
];

/** Response key for the matched employments. Chosen to survive the API's camelization unchanged. */
const EMPLOYEE_MATCHES = 'employees';

// Root-relative paths: inner_hits resolves `_source` includes from the document
// root, so dropping the `employees_nested.` prefix returns an empty object.
const EMPLOYEE_MATCH_FIELDS = [
  'employees_nested.employee_id_keyword',
  'employees_nested.first_name_text',
  'employees_nested.last_name_text',
  'employees_nested.payroll_id_keyword',
  'employees_nested.email_text',
];

/** Only projected when roles were asked for, so the typeahead's payload is unchanged. */
const EMPLOYEE_ROLE_FIELD = 'employees_nested.jurisdictions_keyword';

/** Person-grain names, used only when an employment record carries neither name. */
const PERSON_NAME_FIELDS = ['person_first_name_text', 'person_last_name_text'];

type EmployeeMatch = {
  employeeIdKeyword?: string | number;
  firstNameText?: string;
  lastNameText?: string;
  payrollIdKeyword?: string;
  emailText?: string;
  jurisdictionsKeyword?: string[];
};

export type PersonNames = {
  personFirstNameText?: string;
  personLastNameText?: string;
};

export type EmployeeSearchResult = {
  id: string;
  fullName: string;
  payrollId?: string;
  email?: string;
};

export type CompanyPersonResult = {
  /** Person id — the grain `admin_impersonate` and the person routes take. */
  id: string;
  fullName: string;
  email?: string;
  /** Roles held at the searched company only, never at the person's other employers. */
  roleKeys: string[];
};

/**
 * Builds the company-scoped Person-index search used by employee typeaheads.
 * The outer company_ids predicate satisfies SearchPolicy. The actual company
 * and identity predicates must share one employees_nested object, otherwise a
 * person employed by two companies can match a name in one employment and be
 * returned for the other.
 *
 * The nested identity clause must sit in scoring context, never `filter`:
 * filtered clauses score 0, which ties every fuzzy name match and makes the
 * returned top-N arbitrary — at a large company the person searched for
 * rarely surfaces at all.
 *
 * The trailing name token is usually mid-keystroke, and AUTO fuzziness (two
 * edits) cannot reach a completion — "sant" is three edits from "santers".
 * A boosted clause therefore requires every completed token and
 * prefix-matches the trailing one, outscoring one-token fuzzy matches.
 *
 * `inner_hits` carries the response contract: the person-grain `employees` array
 * spans every company the person works for, so projecting it would ship other
 * employers' records to a browser that may only see this one.
 *
 * @param search - raw typeahead input; tokens are classified into email / identifier / name,
 *   and the trailing name token also matches as a prefix (it is usually mid-keystroke).
 *   Blank lists the company's employments instead of matching any identity.
 * @param companyId - the only company whose employment records may be returned
 * @param limit - maximum people matched, and maximum employments returned per person
 * @param roleKeys - when given, only employments holding one of these
 *   `jurisdictions_keyword` values match. Rides inside the same nested clause as the
 *   company predicate, so a role held at another employer cannot qualify a person here.
 * @returns an OpenSearch request body for the Person index
 */
export function buildCompanyEmployeeSearchBody(
  search: string,
  companyId: string,
  limit = DEFAULT_LIMIT,
  roleKeys?: string[]
) {
  const builder = new OpenSearchQueryBuilder().setPagination(1, limit);
  builder.mustTerm('company_ids', companyId);

  const tokens = search.trim().split(/\s+/).filter(Boolean);
  const nameTokens: string[] = [];
  const identityClauses: FilterClause[] = [];

  for (const token of tokens) {
    const kind = classifySearchToken(token);
    if (kind === 'email') {
      identityClauses.push({
        match: {
          'employees_nested.email_text': { query: token, operator: 'and', _name: 'email' },
        },
      });
    } else if (kind === 'identifier') {
      identityClauses.push({
        term: { 'employees_nested.payroll_id_keyword': { value: token, _name: 'payroll' } },
      });
      identityClauses.push({
        term: { 'employees_nested.employee_id_keyword': { value: token, _name: 'employee_id' } },
      });
    } else {
      nameTokens.push(token);
    }
  }

  const trailing = nameTokens.at(-1)?.toLowerCase();
  if (trailing !== undefined) {
    const phrase = nameTokens.join(' ');
    identityClauses.push({
      multi_match: {
        query: phrase,
        fields: EMPLOYEE_NAME_FIELDS,
        type: 'best_fields',
        operator: 'or',
        fuzziness: 'AUTO',
        lenient: true,
        _name: 'name',
      },
    });
    identityClauses.push({
      multi_match: {
        query: phrase,
        fields: EMPLOYEE_NAME_FIELDS,
        type: 'cross_fields',
        operator: 'and',
        lenient: true,
        _name: 'name_exact',
      },
    });

    // AUTO fuzziness (two edits) cannot reach a completion, so the trailing token prefix-matches.
    const completed = nameTokens.slice(0, -1).join(' ');
    const prefixMust: FilterClause[] = [];
    if (completed) {
      prefixMust.push({
        multi_match: {
          query: completed,
          fields: EMPLOYEE_NAME_FIELDS,
          type: 'cross_fields',
          operator: 'and',
          lenient: true,
          _name: 'name_completed',
        },
      });
    }
    prefixMust.push({
      bool: {
        should: EMPLOYEE_NAME_FIELDS.map((field) => ({
          prefix: { [field]: { value: trailing, _name: 'name_prefix' } },
        })),
        minimum_should_match: 1,
      },
    });
    identityClauses.push({ bool: { boost: 3, must: prefixMust } });
  }

  const nestedFilter: FilterClause[] = [
    { term: { 'employees_nested.company_id_keyword': companyId } },
  ];
  if (roleKeys?.length) {
    nestedFilter.push({ terms: { 'employees_nested.jurisdictions_keyword': roleKeys } });
  }

  builder.addSearchShould({
    nested: {
      path: 'employees_nested',
      // max, not the avg default: rank the person by their best-matching employment.
      score_mode: 'max',
      inner_hits: {
        name: EMPLOYEE_MATCHES,
        size: limit,
        _source: roleKeys?.length
          ? [...EMPLOYEE_MATCH_FIELDS, EMPLOYEE_ROLE_FIELD]
          : EMPLOYEE_MATCH_FIELDS,
      },
      query: {
        bool: {
          filter: nestedFilter,
          // An empty `should` with minimum_should_match 1 matches nothing, so a
          // blank search lists the filtered employments rather than returning none.
          ...(identityClauses.length ? { should: identityClauses, minimum_should_match: 1 } : {}),
        },
      },
    },
  });

  return builder.selectSourceFields(PERSON_NAME_FIELDS).build();
}

/**
 * Maps the matched employments onto typeahead results, de-duplicated by employee id.
 *
 * Every nested document here already satisfied the query's company predicate, so
 * there is no company to re-check — and no other employer's record present to leak.
 *
 * @param hits - the `hits.hits` array of a Person search built by buildCompanyEmployeeSearchBody
 * @returns one result per distinct employee id, in match order
 */
export function employeeSearchResultsFromHits(
  hits: OpenSearchHit<PersonNames>[]
): EmployeeSearchResult[] {
  const results = new Map<string, EmployeeSearchResult>();

  hits.forEach((hit) => {
    const person = hit._source ?? {};
    const personName = [person.personFirstNameText, person.personLastNameText]
      .filter(Boolean)
      .join(' ')
      .trim();

    const matches = hit.innerHits?.[EMPLOYEE_MATCHES]?.hits?.hits ?? [];

    matches.forEach((match) => {
      const employee = (match._source ?? {}) as EmployeeMatch;
      if (employee.employeeIdKeyword == null) return;

      const id = String(employee.employeeIdKeyword);
      const fullName =
        [employee.firstNameText, employee.lastNameText].filter(Boolean).join(' ').trim() ||
        personName;

      results.set(id, {
        id,
        fullName,
        payrollId: employee.payrollIdKeyword,
        email: employee.emailText,
      });
    });
  });

  return [...results.values()];
}

/**
 * Searches one company's employees for the typeahead.
 *
 * @param store - WarpDrive store used to issue the search request
 * @param search - raw typeahead input; a blank term or missing company skips the request
 * @param companyId - the only company whose employment records may be returned
 * @param limit - maximum people matched
 * @returns matching employees, or an empty array when there is nothing to search
 */
export async function searchCompanyEmployees(
  store: Store,
  search: string,
  companyId: string,
  limit = DEFAULT_LIMIT
) {
  const term = search.trim();
  if (!term || !companyId) return [];

  const { content } = (await store.request(
    executeSearchRequest({
      'model-type': 'Person',
      'search-body': buildCompanyEmployeeSearchBody(term, companyId, limit),
    })
  )) as { content: OpenSearchResponse<PersonNames> };

  return employeeSearchResultsFromHits(content.hits?.hits ?? []);
}

/**
 * Maps the matched employments onto person-grain results, one per person.
 *
 * Roles come off the matched employment rather than the person document, so a
 * role the person holds at another employer never appears against this company.
 *
 * @param hits - the `hits.hits` array of a Person search built by buildCompanyEmployeeSearchBody
 * @returns one result per person, in match order; people whose match carried no
 *   employment record are skipped
 */
export function companyPeopleFromHits(hits: OpenSearchHit<PersonNames>[]): CompanyPersonResult[] {
  const results: CompanyPersonResult[] = [];

  hits.forEach((hit) => {
    const matches = hit.innerHits?.[EMPLOYEE_MATCHES]?.hits?.hits ?? [];
    if (!matches.length) return;

    const person = hit._source ?? {};
    const personName = [person.personFirstNameText, person.personLastNameText]
      .filter(Boolean)
      .join(' ')
      .trim();

    const roleKeys = new Set<string>();
    let fullName = '';
    let email: string | undefined;

    matches.forEach((match) => {
      const employee = (match._source ?? {}) as EmployeeMatch;
      (employee.jurisdictionsKeyword ?? []).forEach((role) => roleKeys.add(role));
      fullName ||= [employee.firstNameText, employee.lastNameText].filter(Boolean).join(' ').trim();
      email ??= employee.emailText;
    });

    results.push({
      id: hit._id,
      fullName: fullName || personName || hit._id,
      email,
      roleKeys: [...roleKeys],
    });
  });

  return results;
}
